import { HTMLAttributes, KeyboardEvent, RefObject, SyntheticEvent, useLayoutEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import CircleIcon from '@mui/icons-material/FiberManualRecord';
import VisibilityIcon from '@mui/icons-material/Visibility';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { FacetInfo } from 'common/graphqlTypes';
import { TOKEN_TYPE } from 'common/util/filter/types';
import { tokenizeFilter } from 'common/util/filter/parse';
import { setMark } from 'common/PerformanceLogger';

import IconButton from 'components/common/IconButton/IconButton';
import TruncateWithTooltip from 'components/common/TruncateWithTooltip/TruncateWithTooltip';
import ClearableInput from 'components/common/ClearableInput/ClearableInput';
import { completeFilterState } from 'components/common/Managers/RouteStateManager.state';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { sideBarWidthSelector } from 'components/common/Managers/LayoutStateManager';

import { useFacetSelection } from './FacetList.hooks';
import './FacetList.scss';


type FacetRowProps = {
  name:           string;
  uniqueValues:   number;
  onFacetSelect:  (facetName: string) => void;
  onFacetHover?:  (facetName: string) => void;
  onFacetChecked: (facet: string, isChecked: boolean, ev: SyntheticEvent) => void;
  isSelected:     boolean;
  isChecked:      boolean;
  isQueryField:   boolean;
  index:          number;
} & HTMLAttributes<HTMLDivElement>;

/**
* This component displays the name and number of unique values for a single facet.
*
* @prop name             name of the facet
* @prop uniqueValues     number of unique values for the facet
* @prop onFacetSelect    function to call when facet is clicked
* @prop onFacetHover     function to call when facet is hovered over
* @prop onFacetChecked     function to call when facet checkbox is clicked
* @prop isSelected       if true, this facet is currently selected one (details shown in flyout)
* @prop isChecked        if true, this facet is currently checked as a display field
* @prop isQueryField     if true, this facet appears in a term within the current query
* @prop index            index of facet in the list
*/
function FacetRow({
  name,
  uniqueValues,
  onFacetSelect,
  onFacetHover,
  onFacetChecked,
  isSelected,
  isChecked,
  isQueryField,
  index,
  // Collect rest of the common props, e.g. 'On…' handlers and other `HTMLAttributes<HTMLDivElement>`
  ...rest
}: FacetRowProps): JSX.Element {

  const FONT_CONSTANT = 1.91; // font constant - sans serif helvetica
  const FONT_SIZE = 12;

  const className = classNames('FacetList__Facet', {
    'FacetList__Facet--Selected': isSelected,
    'FacetList__Facet--Matched': isQueryField,
  });

  const sidebarWidth = useRecoilValue(sideBarWidthSelector);
  const handleClick = (ev: SyntheticEvent) => onFacetChecked(name, isChecked, ev);
  // formula to calculate characters per line - CPL = width * font-constant / font-size
  // added a difference to adjust cpl of facet name with respect to sidebar width, facet count and view icon
  // final formula =  width * font-constant / font-size - (length of facet count + '16' (view icon width + safe threshold value) )
  // value 16 is added as safe measure to handle few edge cases where facet value can be all caps
  const charactersPerLine = Math.round(sidebarWidth * FONT_CONSTANT / FONT_SIZE) - (uniqueValues.toString().length + 16);
  // button to add facet to the displayed fields
  const addIcon = <CircleIcon className="FacetList__Add" fontSize="small" />;
  const addButton = (
    <IconButton
      icon={addIcon}
      tooltip="Show in results"
      containerClassName="FacetList__Action"
      onClick={handleClick}
    />
  );

  // button to reove facet from the displayed fields
  const deleteIcon = <VisibilityIcon className="FacetList__Delete" fontSize="small" />;
  const deleteButton = (
    <IconButton
      icon={deleteIcon}
      tooltip="Hide in results"
      containerClassName="FacetList__Action"
      onClick={handleClick}
    />
  );

  return (
    <div>
      <div
        className={className}
        onClick={(): void => onFacetSelect(name)}
        onMouseEnter={(): void => onFacetHover?.(name)}
        data-facet={name}
        role="button"
        tabIndex={0}
        data-test-id={createTestId(ELEMENT.searchSidebarFacet, index)}
        // Attach any common props passed, e.g. 'On…' handlers and other `HTMLAttributes<HTMLDivElement>`
        {...rest}
      >
        {isChecked ? deleteButton : addButton}
        <span className="FacetList__FacetName">
          <TruncateWithTooltip
            text={name}
            maxLength={charactersPerLine}
            id={'facet-' + name}
            testId={createTestId(ELEMENT.searchSidebarFacetName, index)}
            ellipsisPlacement={K.PLACEMENT.MIDDLE}
          />
        </span>
        <span className="FacetList__FacetValuesCount">{uniqueValues}</span>
      </div>
    </div>
  );
}

type FacetListProps = {
  facetList:        Array<FacetInfo>;
  facetFilter:      string;
  onFilterChange:   (filter: string) => void;
  onFacetSelect:    (facetName: string) => void;
  onFacetHover:     (facetName: string) => void;
  onKeyDown:        (event: KeyboardEvent) => void;
  selectedFacet:    string;
  listRef:          RefObject<HTMLDivElement>;
};

/**
 * This component shows a list of facets that were found in the search results. The name and the
 * number of unique values for each facet is shown. The user can view all the facets or just the
 * top 50, and filter the list using an input at the bottom.
 *
 * @prop facetList         list of facet objects
 * @prop facetFilter       current value of facet filter
 * @prop onFilterChange    function to call when value of filter input changes
 * @prop onFacetSelect     function to call when a facet is clicked
 * @prop onFacetHover      function to call when a facet hovered over
 * @prop onKeyDown         function to call when a keyboard event occurs
 * @prop selectedFacet     name of currently selected facet (whose details are shown in popup)
 * @prop listRef           ref to fill in so that FacetDetails can have same height
 */
function FacetList({
  facetList,
  facetFilter,
  onFilterChange,
  onFacetSelect,
  onKeyDown,
  selectedFacet,
  listRef,
}: FacetListProps): JSX.Element {

  useLayoutEffect(() => {
    setMark('facetListRender', { facets: facetList.length }, { debounce: 100 });
  });

  const completeFilter = useRecoilValue(completeFilterState);
  const {
    onMouseMove,
    handleFacetHover,
    handleFacetListMouseLeave,
    isControlledSelectionMode,
  } = useFacetSelection(selectedFacet, onFacetSelect, listRef);

  const [ settings, setSettings ] = useRecoilState(settingsState);

  if (facetList.length === 0 && !facetFilter) {
    return <></>;
  }

  // create a list of facets used in the search so we can highlight them
  const tokens = tokenizeFilter(completeFilter);
  const queryFields = tokens.filter((token) => token.type === TOKEN_TYPE.FIELD).map((token) => (token.text.startsWith('$') ? token.text.substring(1) : token.text));

  // update list of checked values when a single value is checked/unchecked
  const onFacetChecked = (facet: string, isChecked: boolean, event: SyntheticEvent): void => {
    const newFacetsToShow = cloneDeep(settings.additionalFields);
    if (!isChecked) {
      newFacetsToShow.push(facet);
    } else {
      const idx = newFacetsToShow.indexOf(facet);
      if (idx !== -1) {
        newFacetsToShow.splice(idx, 1);
      }
    }

    const newSettings = cloneDeep(settings);
    newSettings.additionalFields = newFacetsToShow;
    setSettings(newSettings);

    // eat event so we don't open FacetDetails popup
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="FacetList" ref={listRef}>
      <div className="FacetList__Header">
        <span>Fields</span>
      </div>
      <div className="FacetList__Filter">
        <ClearableInput
          className="FacetList__Input"
          placeholder="Filter fields"
          size="sm"
          value={facetFilter}
          onChange={(filter: string): void => onFilterChange(filter)}
          testId={ELEMENT.searchSidebarFacetFilter}
        />
      </div>
      <div
        className="FacetList__Facets"
        onKeyDown={onKeyDown}
        onMouseLeave={handleFacetListMouseLeave}
        onMouseMove={onMouseMove}
        role="list"
        data-controlled-focus={isControlledSelectionMode}
        data-test-id={ELEMENT.searchSidebarFacetList}
      >
        {facetList.map((facet, index) => {
          const isChecked = settings.additionalFields.includes(facet.name);
          return (
            <FacetRow
              name={facet.name}
              uniqueValues={facet.uniqueValuesCount}
              key={facet.name}
              onFacetSelect={onFacetSelect}
              onFacetChecked={onFacetChecked}
              onMouseEnter={handleFacetHover}
              isSelected={selectedFacet === facet.name}
              isChecked={isChecked}
              isQueryField={queryFields.includes(facet.name)}
              index={index}
            />
          );
        })}
      </div>
    </div>
  );
}

export default FacetList;
